import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import ProfileSection from './ProfileSection';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Edit, Phone, Mail, LockKeyhole, Check, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
const ProfileMembership: React.FC = () => {
  const {
    user
  } = useAuth();
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Format user email or use placeholder
  const userEmail = user?.email || 'No email available';

  // Get current username from profile or generate default
  const currentUsername = user?.user_metadata?.username || user?.email?.split('@')[0] || 'Not set';
  React.useEffect(() => {
    setUsername(currentUsername);
  }, [currentUsername]);
  const validateUsername = (username: string): { isValid: boolean; error?: string } => {
    const trimmed = username.trim();
    
    if (!trimmed) {
      return { isValid: false, error: 'Username cannot be empty' };
    }
    
    if (trimmed.length < 3 || trimmed.length > 30) {
      return { isValid: false, error: 'Username must be 3-30 characters long' };
    }
    
    if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
      return { isValid: false, error: 'Username can only contain letters, numbers, underscore, and hyphen' };
    }
    
    if (/^[0-9_-]/.test(trimmed)) {
      return { isValid: false, error: 'Username cannot start with a number or special character' };
    }
    
    return { isValid: true };
  };

  const handleUsernameUpdate = async () => {
    if (!user || !username.trim()) return;
    
    // Client-side validation
    const validation = validateUsername(username);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }

    setIsLoading(true);
    try {
      // Update the profile in the profiles table (server-side validation will also run)
      const {
        error: profileError
      } = await supabase.from('profiles').update({
        username: username.trim()
      }).eq('id', user.id);
      if (profileError) throw profileError;

      // Update user metadata
      const {
        error: authError
      } = await supabase.auth.updateUser({
        data: {
          username: username.trim()
        }
      });
      if (authError) throw authError;
      toast.success('Username updated successfully');
      setIsEditingUsername(false);
    } catch (error: any) {
      console.error('Error updating username:', error);
      // Handle specific validation errors from the database
      if (error.message?.includes('Invalid username format')) {
        toast.error('Invalid username format. Please check the requirements.');
      } else if (error.message?.includes('duplicate key')) {
        toast.error('Username already taken. Please choose another.');
      } else {
        toast.error(error.message || 'Failed to update username');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleCancelEdit = () => {
    setUsername(currentUsername);
    setIsEditingUsername(false);
  };
  return <ProfileSection title="Account Details">
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-6 bg-stone-50 rounded-none">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-gray-400" />
                <div className="flex-1">
                  <p className="text-sm text-zinc-700">Username</p>
                   {isEditingUsername ? <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Input 
                          value={username} 
                          onChange={e => setUsername(e.target.value)} 
                          className="h-8 text-sm" 
                          placeholder="Enter username" 
                          disabled={isLoading}
                          maxLength={30}
                        />
                        <Button variant="ghost" size="sm" onClick={handleUsernameUpdate} disabled={isLoading || !username.trim()} className="h-8 w-8 p-0">
                          <Check size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={handleCancelEdit} disabled={isLoading} className="h-8 w-8 p-0">
                          <X size={14} />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        3-30 characters, letters/numbers/underscore/hyphen only
                      </p>
                    </div> : <p className="text-gray-400">{currentUsername}</p>}
                </div>
              </div>
              {!isEditingUsername && <Button variant="ghost" size="sm" onClick={() => setIsEditingUsername(true)} className="bg-slate-700 hover:bg-slate-600 text-slate-50">
                  <Edit size={16} className="mr-1" />
                  Change
                </Button>}
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <Mail size={16} className="text-gray-400" />
                <div>
                  <p className="text-sm text-zinc-700">Email</p>
                  <p className="text-gray-400">{userEmail}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-50 bg-slate-700 hover:bg-slate-600">
                <Edit size={16} className="mr-1" />
                Change
              </Button>
            </div>
            
            <div className="flex items-center justify-between py-2 border-b border-gray-800 bg-zinc-50">
              <div className="flex items-center gap-3">
                <LockKeyhole size={16} className="text-gray-400" />
                <div>
                  <p className="text-sm text-zinc-700">Password</p>
                  <p className="text-gray-400">••••••••</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="bg-slate-700 hover:bg-slate-600 text-slate-50">
                <Edit size={16} className="mr-1" />
                Change
              </Button>
            </div>
            
            
          </div>
        </CardContent>
      </Card>
      
      
    </ProfileSection>;
};
export default ProfileMembership;